import { useEffect, useRef, useState } from "react";
import { X, Send, Loader2 } from "lucide-react";
import { startChat, sendChat } from "../lib/chatApi";
import { trackCtaClick } from "../lib/api";
import {
  trackAddToCart,
  trackInitiateCheckout,
} from "../lib/tiktok";

const STORAGE_KEY = "thabit_chat_session_v1";

const WHATSAPP_GREEN = "#25D366";
const WHATSAPP_DARK = "#075E54";
const WHATSAPP_BG = "#ECE5DD";
const WHATSAPP_BUBBLE_OUT = "#DCF8C6";
const WHATSAPP_BUBBLE_IN = "#FFFFFF";

// Stripe Payment Link kept in sync with LandingGulf.jsx — both target the
// same hosted Thabit checkout. When the bot recommends "buy now", it shows
// this link as a clickable bubble that fires the buy-thabit-chat-* analytics
// label the admin dashboard already aggregates.
const THABIT_STRIPE_URL = "https://buy.stripe.com/aFa3cv16k2vd6GafaCfbq02?locale=ar";

// FAQ quick-reply chips shown under the very first bot greeting. Clicking a
// chip sends that text as a user message; the bot's existing rule-based
// responses (with our rebrand layer) will reply naturally and surface the
// Stripe link only when contextually relevant (e.g. "كم السعر؟", "أبي أبدأ").
const FAQ_CHIPS = [
  "شنو ثابت؟",
  "وش راح أتعلم؟",
  "كم السعر؟",
  "فيه ضمان؟",
  "كيف أستلم بعد الدفع؟",
  "أبي أبدأ الآن",
];

function WhatsAppGlyph({ className = "w-7 h-7" }) {
  return (
    <svg viewBox="0 0 32 32" className={className} fill="currentColor" aria-hidden="true">
      <path d="M16.001 3.2C9.046 3.2 3.4 8.846 3.4 15.801c0 2.276.605 4.486 1.752 6.422L3.2 28.8l6.756-1.917a12.6 12.6 0 0 0 6.045 1.54h.005c6.95 0 12.6-5.646 12.6-12.6 0-3.367-1.31-6.531-3.69-8.91A12.522 12.522 0 0 0 16.001 3.2Zm0 22.957h-.004a10.452 10.452 0 0 1-5.328-1.46l-.382-.227-3.954 1.123 1.063-3.852-.249-.397a10.45 10.45 0 0 1-1.602-5.543c0-5.78 4.701-10.482 10.486-10.482a10.4 10.4 0 0 1 7.412 3.07 10.4 10.4 0 0 1 3.07 7.418c-.002 5.784-4.706 10.486-10.512 10.486Zm5.745-7.847c-.314-.158-1.86-.918-2.149-1.023-.289-.105-.499-.158-.708.158-.21.316-.812 1.022-.995 1.232-.183.21-.367.236-.681.079-.314-.158-1.327-.49-2.527-1.56-.934-.832-1.564-1.86-1.748-2.176-.183-.316-.02-.486.138-.643.142-.141.314-.367.472-.55.158-.184.21-.316.314-.526.105-.21.052-.394-.026-.552-.078-.157-.708-1.706-.97-2.337-.255-.613-.515-.53-.708-.54l-.604-.011a1.158 1.158 0 0 0-.84.394c-.288.316-1.102 1.077-1.102 2.626 0 1.549 1.128 3.046 1.286 3.255.157.21 2.22 3.39 5.378 4.755.751.325 1.337.518 1.793.663.753.24 1.439.206 1.98.125.604-.09 1.86-.76 2.122-1.494.262-.735.262-1.366.183-1.494-.078-.13-.288-.21-.602-.367Z"/>
    </svg>
  );
}

// Replace any backend-supplied legacy URLs (https://bluebook.cx/*) with the
// Stripe Payment Link so the bot drives users straight to checkout. We match
// the WHOLE URL (with any path) in one pass to avoid leaving leftover path
// fragments like "/locale=ar/gulf" when chaining multiple replacements.
function rewriteUrlsToStripe(text) {
  return String(text || "").replace(
    /https?:\/\/bluebook\.cx[^\s]*/g,
    THABIT_STRIPE_URL,
  );
}

// Rebrand legacy "الكتاب الأزرق" / "الكتاب" references in bot replies on the
// fly so we don't have to fork the backend rule database for Thabit. Arabic
// word boundaries (\b) don't work the same as Latin scripts, so we use
// explicit context-aware patterns instead of \bالكتاب\b.
function rebrand(text) {
  let t = String(text || "");
  t = t.replace(/الكتاب الأزرق/g, "ثابت");
  t = t.replace(/الكتاب الرقمي/g, "النسخة الرقمية");
  t = t.replace(/داخل الكتاب/g, "داخل ثابت");
  t = t.replace(/عينة من الكتاب/g, "مقدمة عن ثابت");
  t = t.replace(/محتوى الكتاب/g, "محتوى ثابت");
  t = t.replace(/شاهد العينة/g, "اسمع المقدمة");
  t = t.replace(/يشاهد عينة/g, "يسمع مقدمة");
  t = t.replace(/شاهد عينة/g, "اسمع مقدمة");
  t = t.replace(/تشوف عينة/g, "تسمع مقدمة");
  // Pricing — legacy bot quotes geo-tiered prices (50/55/27) for Bluebook.
  // Thabit is a single-currency flat price (199 ريال) everywhere.
  t = t.replace(
    /السعر\s*50\s*ريال[^\n]*?27\s*دولار[^\n]*/g,
    "السعر 99 ريال سعودي (عرض الإطلاق، بدلاً من 199 ريال) 💎"
  );
  t = t.replace(/50\s*ريال\s*سعودي/g, "99 ريال سعودي");
  t = t.replace(/55\s*درهم\s*إماراتي/g, "99 ريال");
  t = t.replace(/27\s*دولار/g, "99 ريال");
  // Catch any standalone "199 ريال" the bot might mention (anchor only).
  t = t.replace(/199\s*ريال\s*سعودي/g, "99 ريال سعودي");
  // Plain "الكتاب" (with leading whitespace/punctuation, not inside another
  // compound) — match safely without Arabic \b support.
  t = t.replace(/(^|[\s.,؛،:!?\u061f"'«»])الكتاب([\s.,؛،:!?\u061f"'«»]|$)/g, "$1ثابت$2");
  return t;
}

function formatMessageText(text, onLinkClick) {
  const transformed = rewriteUrlsToStripe(rebrand(text));
  const parts = transformed.split(/(https?:\/\/[^\s]+)/g);
  return parts.map((p, i) =>
    /^https?:\/\//.test(p) ? (
      <a
        key={i}
        href={p}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => onLinkClick?.(p)}
        data-testid="chat-bot-link"
        className="underline decoration-2 underline-offset-2 break-all"
        style={{ color: WHATSAPP_DARK }}
      >
        {p}
      </a>
    ) : (
      <span key={i}>{p}</span>
    )
  );
}

export default function ChatWidgetThabit() {
  // Restore from localStorage synchronously via lazy initializers — avoids
  // the set-state-in-effect anti-pattern flagged by react-hooks lint.
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.session_id && Array.isArray(parsed?.messages)) {
          return parsed.session_id;
        }
      }
    } catch (_) { /* ignore */ }
    return null;
  });
  const [messages, setMessages] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.session_id && Array.isArray(parsed?.messages)) {
          return parsed.messages;
        }
      }
    } catch (_) { /* ignore */ }
    return [];
  });
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [booting, setBooting] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Persist on every change.
  useEffect(() => {
    if (!sessionId) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ session_id: sessionId, messages }));
    } catch (_) { /* ignore */ }
  }, [sessionId, messages]);

  // Auto-scroll to the latest message whenever the list grows.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, sending]);

  const openChat = async () => {
    trackCtaClick("thabit-chat-widget-open", "landing-gulf");
    setOpen(true);
    if (!sessionId) {
      setBooting(true);
      try {
        const r = await startChat();
        setSessionId(r.session_id);
        // Friendly opener WITHOUT the Stripe link — the link flows in
        // naturally when the user asks about price/buying via the FAQ chips
        // or free text.
        setMessages([{
          role: "bot",
          text:
            "هلا والله 👋\nأنا مساعد «ثابت» — جلسات صوتية للحضور والثبات في المواقف المهمة.\n\n" +
            "اختر سؤالاً من الأسئلة الشائعة، أو اكتب سؤالك بحرية وأنا أساعدك 👇",
          ts: Date.now(),
          showChips: true,
        }]);
      } catch (e) {
        setMessages([{
          role: "bot",
          text: "هلا والله 👋 اكتب سؤالك وأنا أساعدك. لو ودك تتواصل مباشرة: support@bluebook.cx",
          ts: Date.now(),
          showChips: true,
        }]);
      } finally {
        setBooting(false);
        setTimeout(() => inputRef.current?.focus(), 200);
      }
    } else {
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  };

  // Send a chip's text as if the user typed it. Reuses the same handleSend
  // path so the bot's rule-based answer (with rebrand + URL rewriting) handles
  // the response naturally.
  const handleChipClick = async (chipText) => {
    if (!sessionId || sending) return;
    trackCtaClick("thabit-chat-chip", "landing-gulf");
    setMessages((prev) => [
      // hide the chips on previous bot messages once one is clicked
      ...prev.map((m) => (m.showChips ? { ...m, showChips: false } : m)),
      { role: "user", text: chipText, ts: Date.now() },
    ]);
    setSending(true);
    try {
      const r = await sendChat(sessionId, chipText);
      setMessages((prev) => [...prev, { role: "bot", text: r.reply, ts: Date.now() }]);
    } catch (err) {
      setMessages((prev) => [...prev, {
        role: "bot",
        text: "عذراً، حصل خلل بسيط. جرب مرة ثانية أو راسلنا support@bluebook.cx",
        ts: Date.now(),
      }]);
    } finally {
      setSending(false);
    }
  };

  const handleSend = async (e) => {
    e?.preventDefault?.();
    const text = input.trim();
    if (!text || sending || !sessionId) return;
    setInput("");
    setMessages((prev) => [
      // Hide chips when user types their own question.
      ...prev.map((m) => (m.showChips ? { ...m, showChips: false } : m)),
      { role: "user", text, ts: Date.now() },
    ]);
    setSending(true);
    trackCtaClick("thabit-chat-message-sent", "landing-gulf");
    try {
      const r = await sendChat(sessionId, text);
      setMessages((prev) => [...prev, { role: "bot", text: r.reply, ts: Date.now() }]);
    } catch (err) {
      setMessages((prev) => [...prev, {
        role: "bot",
        text: "عذراً، حصل خلل بسيط. جرب مرة ثانية أو راسلنا support@bluebook.cx",
        ts: Date.now(),
      }]);
    } finally {
      setSending(false);
    }
  };

  // Every link click is attributed. A click on the Stripe Payment Link is
  // tagged with `buy-thabit-chat` so it shows up alongside header/hero/sticky
  // breakdowns in the admin "Thabit — Stripe" section. We ALSO fire TikTok's
  // AddToCart + InitiateCheckout standard events so the pixel learns from
  // chat-driven purchase intent the same way it does from page CTAs.
  const handleLinkClick = (url) => {
    if (url.startsWith(THABIT_STRIPE_URL) || /buy\.stripe\.com/.test(url)) {
      trackCtaClick("buy-thabit-chat", "landing-gulf");
      try {
        trackAddToCart("thabit_audio");
        trackInitiateCheckout("thabit_audio");
      } catch (_) { /* never block link */ }
    } else {
      trackCtaClick("thabit-chat-link-other", "landing-gulf");
    }
  };

  return (
    <>
      {!open && (
        <button
          type="button"
          onClick={openChat}
          data-testid="thabit-chat-widget-launcher"
          className="fixed bottom-44 right-4 sm:bottom-5 z-[80] w-12 h-12 rounded-full flex items-center justify-center shadow-[0_8px_22px_rgba(0,0,0,0.4)] hover:shadow-[0_10px_28px_rgba(37,211,102,0.55)] transition-all hover:scale-[1.06]"
          style={{ backgroundColor: WHATSAPP_GREEN, color: "#fff" }}
          aria-label="تحدث مع ثابت"
        >
          <WhatsAppGlyph className="w-6 h-6 text-white" />
          {/* unread dot — purely decorative to draw the eye */}
          <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-red-500 border-2 border-white animate-pulse" />
        </button>
      )}

      {open && (
        <div
          className="fixed inset-x-3 bottom-3 sm:bottom-5 sm:right-5 sm:left-auto sm:w-[380px] sm:h-[560px] h-[72vh] z-[100] rounded-xl overflow-hidden shadow-[0_30px_70px_rgba(0,0,0,0.65)] flex flex-col"
          data-testid="thabit-chat-widget-panel"
          style={{ backgroundColor: WHATSAPP_BG }}
          dir="rtl"
        >
          <div
            className="flex items-center gap-3 px-3 py-2.5 text-white"
            style={{ backgroundColor: WHATSAPP_DARK }}
          >
            <div className="w-10 h-10 rounded-full bg-[#C5A059] flex items-center justify-center text-[#040914] font-black text-sm shrink-0">
              ث
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold leading-tight">ثابت — المساعد</div>
              <div className="text-[11px] opacity-85 leading-tight flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#25D366]" />
                متصل الآن · جلسات صوتية
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-white/85 hover:text-white p-1.5 rounded-full hover:bg-white/10"
              aria-label="إغلاق"
              data-testid="thabit-chat-widget-close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-3 py-4 space-y-2"
            data-testid="thabit-chat-widget-messages"
            style={{
              backgroundImage: "url('data:image/svg+xml;utf8,<svg xmlns=\"http://www.w3.org/2000/svg\" width=\"40\" height=\"40\" viewBox=\"0 0 40 40\"><circle cx=\"2\" cy=\"2\" r=\"0.6\" fill=\"%23000\" opacity=\"0.04\"/></svg>')",
            }}
          >
            {booting && (
              <div className="flex items-center justify-center py-6 text-[#075E54]/70 text-xs gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>جارٍ التحضير...</span>
              </div>
            )}

            {messages.map((m, i) => {
              const mine = m.role === "user";
              return (
                <div key={i}>
                  <div className={`flex ${mine ? "justify-start" : "justify-end"}`}>
                    <div
                      className={`max-w-[80%] rounded-lg px-3 py-2 text-[13.5px] leading-relaxed whitespace-pre-wrap shadow-sm ${mine ? "rounded-bl-sm" : "rounded-br-sm"}`}
                      style={{
                        backgroundColor: mine ? WHATSAPP_BUBBLE_OUT : WHATSAPP_BUBBLE_IN,
                        color: "#111",
                      }}
                      data-testid={`thabit-chat-msg-${m.role}-${i}`}
                    >
                      {formatMessageText(m.text, handleLinkClick)}
                    </div>
                  </div>
                  {/* FAQ chips render once under the first bot greeting and
                      disappear as soon as the user picks one (or types). */}
                  {m.showChips && !mine && (
                    <div className="mt-2 flex flex-wrap gap-1.5 justify-end" data-testid="thabit-chat-faq-chips">
                      {FAQ_CHIPS.map((chip) => (
                        <button
                          key={chip}
                          type="button"
                          onClick={() => handleChipClick(chip)}
                          disabled={sending}
                          data-testid={`thabit-chat-chip-${chip}`}
                          className="text-[12px] font-semibold px-3 py-1.5 rounded-full bg-white border border-[#075E54]/25 text-[#075E54] hover:bg-[#075E54] hover:text-white transition-colors disabled:opacity-50"
                        >
                          {chip}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            {sending && (
              <div className="flex justify-end">
                <div
                  className="rounded-lg rounded-br-sm px-3 py-2 shadow-sm flex items-center gap-1.5"
                  style={{ backgroundColor: WHATSAPP_BUBBLE_IN }}
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-[#075E54]/60 animate-bounce" style={{ animationDelay: "0s" }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-[#075E54]/60 animate-bounce" style={{ animationDelay: "0.15s" }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-[#075E54]/60 animate-bounce" style={{ animationDelay: "0.3s" }} />
                </div>
              </div>
            )}
          </div>

          <form
            onSubmit={handleSend}
            className="flex items-center gap-2 px-2.5 py-2 bg-[#F0F0F0] border-t border-black/5"
            data-testid="thabit-chat-widget-form"
          >
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="اكتب رسالتك..."
              dir="rtl"
              maxLength={1000}
              disabled={sending || booting}
              data-testid="thabit-chat-widget-input"
              className="flex-1 bg-white text-[#111] text-sm rounded-full px-4 py-2.5 outline-none border border-black/5 focus:border-[#075E54]/40 placeholder:text-[#666] placeholder:text-[13px]"
            />
            <button
              type="submit"
              disabled={sending || booting || !input.trim()}
              data-testid="thabit-chat-widget-send"
              aria-label="إرسال"
              className="w-10 h-10 rounded-full flex items-center justify-center text-white shrink-0 transition-opacity disabled:opacity-50"
              style={{ backgroundColor: WHATSAPP_GREEN }}
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </form>
        </div>
      )}
    </>
  );
}
